import styled, { css, StyledProps } from 'styled-components';
import {
  thickTwoLayeredBoxShadow,
  twoLayeredBoxShadow,
} from '../../commonStyles/styling';
import colors from '../../helpers/colors';
import fonts from '../../helpers/fonts';
import mediaQuery from '../../helpers/mediaQuery';
import spacing from '../../helpers/spacing';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type Background = 'light' | 'dark' | 'transparent';

export type StyleProps = StyledProps<{
  disabled?: boolean;
  variant: ButtonVariant;
  background: Background;
}>;

const lightBackgroundNonDisabledStyles = css<StyleProps>`
  &:hover,
  &:focus-visible {
    border-color: ${colors.core02};
    box-shadow: ${colors.core02} 0px 0px 0px 1px inset;
    text-decoration: none;
  
    ${({ variant, theme }) =>
      variant === 'primary'
        ? `background: ${theme.colors.core02};`
        : `& {
            color: ${theme.colors.core02};
          }`}
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 1px ${colors.core02},
    ${twoLayeredBoxShadow(colors.neutral08, colors.core02)};
  }

  &:active {
    border-color: ${colors.neutral02};
  
    ${({ variant, theme }) =>
      variant === 'primary'
        ? `background: ${theme.colors.neutral02};`
        : `& { 
            color: ${theme.colors.neutral02};
          }`}
  
    box-shadow: ${twoLayeredBoxShadow(colors.neutral08, colors.core02)};
  }
`;

const lightBackgroundDisabledStyles = css<StyleProps>`
  cursor: not-allowed;
  text-decoration: none;

  ${({ variant, theme }) =>
    variant === 'primary' && `background: ${theme.colors.neutral04};`};
  & {
    color: ${colors.neutral01};
  }
  border-color: ${({ variant, theme }) =>
    variant === 'primary' ? theme.colors.neutral04 : theme.colors.neutral01};

  /* Quirk: Primary button only has opacity on contents, others include the background */
  ${({ variant }) => (variant === 'primary' ? '> * { opacity: 0.4; }' : 'opacity: 0.4;')}
`;

const lightBackgroundStyles = css<StyleProps>`
  border: ${colors.core01} solid ${({ variant }) => (variant === 'tertiary' ? 2 : 1)}px;
  background: ${({ variant, theme }) =>
    variant === 'primary' ? theme.colors.core01 : theme.colors.neutral08};

  & {
    color: ${({ variant, theme }) =>
      variant === 'primary' ? theme.colors.neutral08 : theme.colors.core01};
  }

  ${({ disabled }) => !disabled && lightBackgroundNonDisabledStyles}

  ${({ disabled }) => disabled && lightBackgroundDisabledStyles}
`;

const darkBackgroundNonDisabledStyles = css<StyleProps>`
  &:hover,
  &:focus-visible {
    // Override colours from the theme fonts
    & {
      color: ${colors.neutral08};
    }
    border-color: ${({ variant, theme }) =>
      variant === 'primary' ? theme.colors.core02 : theme.colors.neutral08};
    box-shadow: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme.colors.core02
        : theme.colors.neutral08} 0px 0px 0px 1px inset;
    text-decoration: none;

    ${({ variant, theme }) =>
      variant === 'primary' && `background: ${theme.colors.core02};`}
  }

  &:focus-visible {
    box-shadow: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme.colors.core02
        : theme.colors.neutral08} 0px 0px 0px 1px inset, 
        ${twoLayeredBoxShadow(colors.core01, colors.neutral08)};
  }

  &:active {
    border-color: ${colors.neutral02};

    ${({ variant, theme }) =>
      variant === 'primary'
        ? `background: ${theme.colors.neutral02};`
        : `
          background: ${theme.colors.neutral08};
          & {
            color: ${theme.colors.neutral02};
          }
        `}

    box-shadow: ${twoLayeredBoxShadow(colors.core01, colors.neutral08)};
  }
`;

const darkBackgroundDisabledStyles = css<StyleProps>`
  cursor: not-allowed;
  text-decoration: none;

  ${({ variant, theme }) =>
    variant === 'primary'
      ? `
      background: ${theme.colors.neutral04};
      border-color: ${theme.colors.neutral04};
      & {
        color: ${theme.colors.neutral01};
      }
    `
      : `
      border-color: ${theme.colors.neutral04};
      & {
        color: ${theme.colors.neutral04};
      }
    `};

  opacity: 0.4;
`;

const darkBackgroundStyles = css<StyleProps>`
  border: ${colors.neutral08} solid ${({ variant }) => (variant === 'tertiary' ? 2 : 1)}px;
  background: ${({ variant, theme }) =>
    variant === 'primary' ? theme.colors.neutral08 : theme.colors.core01};

  // Override colours from the theme fonts
  & {
    color: ${({ variant, theme }) =>
      variant === 'primary' ? theme.colors.core01 : theme.colors.neutral08};
  }

  ${({ disabled }) => !disabled && darkBackgroundNonDisabledStyles}

  ${({ disabled }) => disabled && darkBackgroundDisabledStyles}
`;

// 11 September 2024 - CDS-644
// Current designs do not specify transparent versions of PRIMARY variant.
// The colours used below for PRIMARY variant are temporarily taken from
// the dark background styles above.
const transparentBackgroundNonDisabledStyles = css<StyleProps>`
  &:hover,
  &:focus-visible {
    // Override colours from the theme fonts
    & {
      color: ${colors.neutral08};
    }
    border-color: ${({ variant, theme }) =>
      variant === 'primary' ? theme.colors.core02 : theme.colors.neutral08};
    text-decoration: none;

    ${({ variant, theme }) =>
      variant === 'primary' && `background: ${theme.colors.core02};`}
  }

  &:focus-visible {
    border-width: 1px;
    box-shadow: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme.colors.core02
        : theme.colors.neutral08} 0px 0px 0px 0px inset, 
        ${thickTwoLayeredBoxShadow(colors.core01, colors.neutral08)};
  }

  &:hover {
    border-width: 2px;
  }


  &:active {
    border-color: ${colors.neutral08};
    border-width: 1px;

    ${({ variant, theme }) =>
      variant === 'primary'
        ? `background: ${theme.colors.neutral02};`
        : `
          background: transparent;
        `}

    box-shadow: ${twoLayeredBoxShadow(colors.core01, colors.neutral08)};
  }
`;

const transparentBackgroundDisabledStyles = css<StyleProps>`
  cursor: not-allowed;
  text-decoration: none;

  ${({ variant, theme }) =>
    variant === 'primary'
      ? `
      background: ${theme.colors.neutral04};
      border-color: ${theme.colors.neutral04};
      & {
        color: ${theme.colors.neutral01};
      }
    `
      : `
      border-color: ${theme.colors.neutral04};
      & {
        color: ${theme.colors.neutral04};
      }
    `};

  opacity: 0.4;
`;

const transparentBackgroundStyles = css<StyleProps>`
  border: ${colors.neutral08} solid 1px;
  background: transparent;

  // Override colours from the theme fonts
  & {
    color: ${({ variant, theme }) =>
      variant === 'primary' ? theme.colors.core01 : theme.colors.neutral08};
  }

  ${({ disabled }) => !disabled && transparentBackgroundNonDisabledStyles}

  ${({ disabled }) => disabled && transparentBackgroundDisabledStyles}
`;

export const ButtonStyle = styled.button<StyleProps>`
  display: flex;
  align-items: center;
  cursor: pointer;

  ${({ variant }) =>
    variant !== 'tertiary' &&
    css`
      min-height: ${spacing(8)};
    `}

  ${({ variant }) =>
    variant === 'tertiary' &&
    css`
      min-height: ${spacing(6)};
    `}

  ${mediaQuery.tabletPortrait`
    ${({ variant }) =>
      variant === 'tertiary' &&
      css`
        min-height: ${spacing(5)};
      `}
    min-width: 0;
  `}

  padding: 0 ${spacing(2)};

  transition: all .3s ease-out;

  ${fonts.paragraph}

  border-radius: ${({ theme }) => theme.buttonRadius};

  /* Disable browser default focus behaviour */
  outline: none;

  text-decoration: ${({ variant }) => (variant === 'tertiary' ? 'underline' : 'none')};

  ${({ background }) =>
    background === 'light'
      ? lightBackgroundStyles
      : background === 'dark'
      ? darkBackgroundStyles
      : transparentBackgroundStyles}
`;

// Buttons may not be flex containers in some browsers
// so use an inner container instead.
export const InnerStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing(1)};
  height: 100%;
  width: 100%;
`;
